import { useEffect, useState } from 'react';

const HomePage = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-20">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center">
              <img src="https://public.readdy.ai/ai/img_res/0cde27b8-035c-4d6e-b7fe-8018fcded211.png" alt="DAR d.o.o." className="h-12 w-auto" />
            </div>
            <div className="flex items-center space-x-10">
              <button onClick={() => scrollToSection('home')} className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/80 hover:text-white'}`}>Home</button>
              <button onClick={() => scrollToSection('about')} className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/80 hover:text-white'}`}>About</button>
              <button onClick={() => scrollToSection('products')} className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/80 hover:text-white'}`}>Products</button>
              <button onClick={() => scrollToSection('contact')} className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/80 hover:text-white'}`}>Contact</button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-b from-[#0A1128] to-[#1E293B]">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="absolute inset-0" style={{ backgroundImage: 'url(https://public.readdy.ai/ai/img_res/2f6649e74f1b836a89380aa57338a118.jpg)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.15 }}></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-20 py-32 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16 items-center">
            <div className="lg:col-span-3">
              <div className="w-32 h-1 bg-[#F97316] mb-6"></div>
              <h1 className="text-7xl font-bold text-white leading-tight mb-8" style={{ letterSpacing: '-0.02em' }}>
                Crafting Premium iOS Experiences
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed max-w-2xl">
                Expert team developing cutting-edge applications for enhanced security and performance on the iOS platform
              </p>
            </div>
            <div className="lg:col-span-2 relative">
              <div className="relative">
                <img src="https://readdy.ai/api/search-image?query=Modern%20iPhone%20mockup%20displaying%20sleek%20VPN%20and%20cleaner%20app%20interface%20with%20elegant%20dark%20theme%20UI%20showing%20security%20features%20and%20performance%20metrics%20on%20a%20transparent%20background%20with%20professional%20lighting%20and%20subtle%20shadows%20emphasizing%20premium%20iOS%20design&width=600&height=800&seq=hero-phone-001&orientation=portrait" alt="iOS Apps" className="w-full h-auto rounded-3xl shadow-2xl" style={{ filter: 'drop-shadow(0 40px 60px rgba(0,0,0,0.4))' }} />
              </div>
            </div>
          </div>
        </div>
        
        <div className="absolute bottom-0 left-0 right-0 text-center pb-16">
          <div className="text-white/5 text-[180px] font-black leading-none select-none">iOS</div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="bg-[#FAF9F6] py-32">
        <div className="max-w-7xl mx-auto px-20">
          <div className="mb-12">
            <span className="inline-block px-5 py-2 border border-gray-300 rounded-full text-xs uppercase tracking-wider text-gray-700">About Us</span>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-1 gap-20">
            <div>
              <h2 className="text-6xl font-serif text-gray-900 leading-tight mb-10" style={{ fontFamily: 'Playfair Display, serif' }}>
                Building Trust Through Innovation
              </h2>
              <div className="space-y-6 text-lg text-gray-600 leading-relaxed max-w-3xl">
                <p>
                  DAR d.o.o. is a team of experts dedicated to developing modern applications for the iOS platform. We combine technical excellence with creative innovation to deliver products that truly make a difference in users' daily lives.
                </p>
                <p>
                  Our products, such as VPN and Cleaner, are designed to enhance user experience and improve the security of mobile devices. We believe in creating solutions that are not only powerful but also intuitive and accessible to everyone.
                </p>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-16">
            <div className="rounded-3xl overflow-hidden border-8 border-gray-100">
              <img src="https://readdy.ai/api/search-image?query=Professional%20software%20development%20team%20collaborating%20on%20iOS%20applications%20in%20modern%20office%20environment%20with%20MacBooks%20and%20iPhones%20showing%20teamwork%20and%20innovation%20with%20bright%20natural%20lighting%20and%20contemporary%20workspace%20design%20emphasizing%20technology%20and%20creativity&width=800&height=600&seq=about-team-001&orientation=landscape" alt="Team Collaboration" className="w-full h-96 object-cover object-top" />
            </div>
            <div className="rounded-3xl overflow-hidden border-8 border-gray-100">
              <img src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20office%20workspace%20with%20clean%20desk%20setup%20featuring%20Apple%20devices%20and%20iOS%20development%20tools%20with%20natural%20light%20and%20plants%20creating%20a%20productive%20and%20inspiring%20environment%20for%20mobile%20app%20development%20with%20contemporary%20design%20elements&width=800&height=600&seq=about-office-001&orientation=landscape" alt="Office Workspace" className="w-full h-96 object-cover object-top" />
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="bg-gradient-to-b from-[#0F172A] to-[#1E293B] py-36">
        <div className="max-w-7xl mx-auto px-20">
          <div className="text-center mb-20">
            <div className="text-[#F97316] text-sm uppercase tracking-wider mb-4">Our Products</div>
            <h2 className="text-6xl font-bold text-white">Powerful Solutions for iOS</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="bg-[#0A0A0A] rounded-3xl p-12">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mb-8">
                <i className="ri-shield-check-line text-white text-3xl"></i>
              </div>
              <h3 className="text-4xl font-bold text-white mb-6">VPN</h3>
              <p className="text-gray-400 text-base leading-relaxed max-w-md mb-12">
                Advanced encryption technology protecting your privacy and securing your connection across all networks with military-grade security protocols
              </p>
              <div className="w-16 h-0.5 bg-[#F97316]"></div>
            </div>
            
            <div className="relative rounded-3xl overflow-hidden p-12 min-h-[400px]" style={{ backgroundImage: 'url(https://readdy.ai/api/search-image?query=Clean%20modern%20iOS%20interface%20showing%20device%20optimization%20and%20cleaning%20features%20with%20minimalist%20design%20elements%20storage%20management%20and%20performance%20metrics%20on%20simple%20light%20background%20emphasizing%20clarity%20and%20efficiency%20with%20professional%20mobile%20app%20aesthetics&width=800&height=600&seq=product-cleaner-001&orientation=landscape)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
              <div className="relative z-10">
                <div className="inline-flex items-center space-x-3 bg-black/60 backdrop-blur-sm rounded-full px-5 py-2 mb-auto">
                  <i className="ri-brush-line text-white text-xl"></i>
                  <span className="text-white text-sm font-medium">Cleaner</span>
                </div>
              </div>
              <div className="absolute bottom-12 left-12 right-12 z-10">
                <h3 className="text-3xl font-bold text-white">Optimize Your Device</h3>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="bg-[#F5F3EF] py-28">
        <div className="max-w-7xl mx-auto px-20">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-serif text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>Why Choose Our Solutions</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <div className="bg-[#0A0A0A] rounded-3xl p-10">
              <div className="w-14 h-14 rounded-full bg-gray-800 flex items-center justify-center mb-6">
                <i className="ri-lock-line text-white text-2xl"></i>
              </div>
              <h3 className="text-3xl font-bold text-white mb-4">Bank-Level Encryption</h3>
              <p className="text-gray-400 text-sm">Military-grade security protocols ensuring your data remains protected at all times</p>
            </div>
            
            <div className="relative rounded-3xl overflow-hidden p-10 min-h-[320px]" style={{ backgroundImage: 'url(https://readdy.ai/api/search-image?query=Performance%20metrics%20and%20speed%20graphs%20showing%20fast%20data%20processing%20with%20lightning%20bolt%20symbols%20and%20optimization%20indicators%20on%20clean%20gradient%20background%20emphasizing%20efficiency%20and%20high%20performance%20with%20modern%20digital%20dashboard%20design%20elements&width=600&height=600&seq=feature-performance-001&orientation=squarish)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
              <div className="relative z-10">
                <div className="inline-flex items-center space-x-2 bg-black/60 backdrop-blur-sm rounded-full px-4 py-2 mb-auto">
                  <i className="ri-flashlight-line text-white text-lg"></i>
                  <span className="text-white text-xs font-medium">Performance</span>
                </div>
              </div>
              <div className="absolute bottom-10 left-10 right-10 z-10">
                <h3 className="text-3xl font-bold text-white">Lightning Fast</h3>
              </div>
            </div>
            
            <div className="bg-white rounded-3xl p-10">
              <div className="mb-6">
                <img src="https://readdy.ai/api/search-image?query=Customer%20support%20illustration%20with%20headset%20and%20communication%20symbols%20showing%2024%2F7%20availability%20and%20assistance%20in%20minimalist%20style%20with%20friendly%20colors%20and%20modern%20design%20elements%20emphasizing%20reliability%20and%20professional%20service&width=400&height=300&seq=feature-support-001&orientation=landscape" alt="Support" className="w-full h-32 object-cover object-top rounded-xl" />
              </div>
              <h3 className="text-3xl font-serif text-gray-900 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>24/7 Support</h3>
              <p className="text-gray-600 text-sm">Always here to help you with any questions or concerns</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="bg-white py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-[#0F172A] rounded-3xl p-20 relative overflow-hidden">
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 text-[#1A2332] text-[120px] font-black leading-none select-none whitespace-nowrap">DAR</div>
            
            <div className="relative z-10 grid grid-cols-1 md:grid-cols-4 gap-12">
              <div>
                <h3 className="text-3xl font-bold text-white mb-10">DAR d.o.o.</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  A team of experts developing modern applications for the iOS platform, dedicated to enhancing user experience and mobile device security.
                </p>
              </div>
              
              <div>
                <h4 className="text-lg text-white mb-6">Location</h4>
                <div className="space-y-3 text-sm text-gray-400">
                  <div className="flex items-start space-x-2">
                    <i className="ri-map-pin-line text-base mt-0.5 flex-shrink-0"></i>
                    <span>Vinogradska cesta 2f</span>
                  </div>
                  <div className="pl-6">Slavonski Brod</div>
                  <div className="pl-6">35000</div>
                  <div className="pl-6">Croatia</div>
                </div>
              </div>
              
              <div>
                <h4 className="text-lg text-white mb-6">Get in Touch</h4>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <i className="ri-mail-line text-lg text-gray-400"></i>
                    <a href="mailto:help@dar-doo.com" className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer">help@dar-doo.com</a>
                  </div>
                  <div className="flex items-center space-x-3">
                    <i className="ri-phone-line text-lg text-gray-400"></i>
                    <a href="tel:+13527882704" className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer">+1 3527882704</a>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="text-lg text-white mb-6">Stay Updated</h4>
                <p className="text-sm text-gray-400 mb-5">Subscribe to our newsletter</p>
                <form id="contact-form" data-readdy-form action="https://readdy.ai/api/form/d69h1pma728k8ctu2o80" method="POST" className="relative" onSubmit={(e) => {
                  e.preventDefault();
                  const form = e.currentTarget;
                  const emailInput = form.querySelector('input[name="email"]') as HTMLInputElement;
                  
                  if (!emailInput.value || !emailInput.validity.valid) {
                    alert('Please enter a valid email address');
                    return;
                  }
                  
                  const formData = new FormData(form);
                  fetch('https://readdy.ai/api/form/d69h1pma728k8ctu2o80', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: new URLSearchParams(formData as any).toString()
                  }).then(() => {
                    alert('Thank you for subscribing!');
                    form.reset();
                  }).catch(() => {
                    alert('Something went wrong. Please try again.');
                  });
                }}>
                  <input 
                    type="email" 
                    name="email"
                    placeholder="Your email address" 
                    required
                    className="w-full bg-[#1E293B] border border-gray-700 rounded-xl px-4 py-3 pr-14 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#F97316] transition-colors"
                  />
                  <button type="submit" className="absolute right-1 top-1 w-10 h-10 bg-[#F97316] rounded-full flex items-center justify-center hover:bg-[#ea580c] transition-colors cursor-pointer">
                    <i className="ri-arrow-right-line text-white text-lg"></i>
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white py-6 text-center">
        <p className="text-sm text-gray-600">
          © 2025 DAR d.o.o. All rights reserved. | <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-gray-900 hover:text-[#F97316] transition-colors cursor-pointer">Powered by Readdy</a>
        </p>
      </footer>
    </div>
  );
};

export default HomePage;